import type { TrainingProgressView } from '../../../hooks/useTrainingProgress';
import { MetricBlock } from '../../ui/MetricBlock';
import { ProgressBar } from '../../ui/ProgressBar';

/**
 * At-a-glance totals above the training matrix: how many people are fully done,
 * and the module-completion split (passed / in progress / not started). Derived
 * entirely from the rows already loaded, so it reflects the active filters.
 * Optional (self-enrolled) modules are excluded, matching the per-person DONE
 * roll-up.
 */
export function ProgressSummary({ view }: { view: TrainingProgressView }) {
  const rows = view.people;
  const totalPeople = rows.length;
  const peopleWithReq = rows.filter((p) => p.summary.assigned > 0);
  const complete = peopleWithReq.filter((p) => p.summary.passed === p.summary.assigned).length;
  const peoplePct =
    peopleWithReq.length > 0 ? Math.round((complete / peopleWithReq.length) * 100) : 0;

  let passed = 0;
  let inProgress = 0;
  let notStarted = 0;
  for (const p of rows) {
    for (const cell of Object.values(p.cells)) {
      if (cell.optional) continue;
      if (cell.status === 'passed') passed += 1;
      else if (cell.status === 'in_progress') inProgress += 1;
      else notStarted += 1;
    }
  }
  const totalAssigned = passed + inProgress + notStarted;
  const modulePct = totalAssigned > 0 ? Math.round((passed / totalAssigned) * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricBlock label="People" value={totalPeople} hint="in this view" />
        <MetricBlock
          label="Fully complete"
          value={`${peoplePct}%`}
          hint={`${complete} of ${peopleWithReq.length} people`}
        />
        <MetricBlock
          label="Passed"
          value={passed}
          hint={`of ${totalAssigned} module${totalAssigned === 1 ? '' : 's'} assigned`}
        />
        <MetricBlock
          label="Outstanding"
          value={inProgress + notStarted}
          hint={`${inProgress} in progress · ${notStarted} not started`}
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-fg-muted">
          <span>Module completion</span>
          <span className="tabular-nums">{modulePct}%</span>
        </div>
        <ProgressBar
          value={passed}
          total={totalAssigned}
          ariaLabel={`${passed} of ${totalAssigned} module completions`}
        />
      </div>
    </div>
  );
}
